/**
 * Institution Portal — dashboard for college users.
 *
 * Layout:
 * - Header with college name + user email + logout
 * - Student table scoped to `assigned_college = user.college_id`
 * - Row click opens a right-side Sheet with the student profile + documents,
 *   plus action buttons per pipeline stage:
 *     application_sent      → "Start Review" or "Verify All Docs" / "Flag Missing"
 *     docs_under_review     → same actions; verification blocked while any
 *                              missing_docs row is unresolved
 *     docs_verified         → "Send Enrollment Agreement" (opens EA modal)
 *     enrollment_agreement_sent  → status pill "Awaiting signature"
 *     enrollment_agreement_signed → green pill + "Send COE" button
 *     coe_sent_money_received → green pill "COE sent"
 * - EA + COE modals: pre-filled subject + editable body + PDF upload
 */
import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '@/contexts/AuthContext';
import { API } from '@/config/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import {
  SignOut, MagnifyingGlass, CheckCircle, WarningCircle,
  PaperPlaneTilt, FileText, ArrowClockwise, ArrowsClockwise, CircleNotch, GraduationCap, Sparkle,
  Clock, Image as ImageIcon, FilePdf, Eye, ChatCircleDots, SquaresFour, ListBullets,
  UploadSimple, CurrencyDollar, DownloadSimple,
} from '@phosphor-icons/react';
import NotificationBell from '@/components/NotificationBell';
import FinancialsModal, { FinancialsSummary } from '@/components/FinancialsModal';
import PrepareEAModal from '@/components/PrepareEAModal';
import BulkPrepareEAModal from '@/components/BulkPrepareEAModal';
import BulkEaFromSheetModal from '@/components/BulkEaFromSheetModal';
import ManualSyncTab from '@/pages/institution/ManualSyncTab';
import SignedEAsTab from '@/pages/institution/SignedEAsTab';
import InstitutionPreAuthorizationTab from '@/pages/institution/PreAuthorizationTab';
// import ScoreReportsSection from '@/pages/institution/ScoreReportsSection';  // retired — reports now surface in Documents
import IntakeAccordion, { GroupByIntakeToggle, useGroupByIntake } from '@/components/IntakeAccordion';

const STAGE_STYLES = {
  profile_created: { color: 'bg-blue-100 text-blue-800 border-blue-300', label: 'New — Awaiting Review', dot: 'bg-blue-500' },
  application_sent: { color: 'bg-slate-100 text-slate-700 border-slate-200', label: 'Application Sent', dot: 'bg-slate-500' },
  docs_under_review: { color: 'bg-amber-100 text-amber-800 border-amber-300', label: 'Docs Under Review', dot: 'bg-amber-500' },
  docs_verified: { color: 'bg-emerald-100 text-emerald-800 border-emerald-300', label: 'Docs Verified', dot: 'bg-emerald-500' },
  enrollment_agreement_sent: { color: 'bg-indigo-100 text-indigo-800 border-indigo-300', label: 'EA Sent', dot: 'bg-indigo-500' },
  enrollment_agreement_signed: { color: 'bg-emerald-100 text-emerald-800 border-emerald-300', label: 'EA Signed ✓', dot: 'bg-emerald-500' },
  sabc_in_process: { color: 'bg-fuchsia-100 text-fuchsia-800 border-fuchsia-300', label: 'SABC In Process', dot: 'bg-fuchsia-500' },
  coe_sent: { color: 'bg-cyan-100 text-cyan-800 border-cyan-300', label: 'COE Sent', dot: 'bg-cyan-500' },
  half_money_received: { color: 'bg-lime-100 text-lime-900 border-lime-300', label: '1/2 Money Received', dot: 'bg-lime-500' },
  second_money_received: { color: 'bg-emerald-100 text-emerald-800 border-emerald-300', label: '2/2 Money Received ✓', dot: 'bg-emerald-600' },
  rejected: { color: 'bg-rose-100 text-rose-800 border-rose-300', label: 'Rejected', dot: 'bg-rose-500' },
  withdrawn: { color: 'bg-slate-100 text-slate-700 border-slate-300', label: 'Withdrawn', dot: 'bg-slate-500' },
};

const StagePill = ({ stage }) => {
  const s = STAGE_STYLES[stage] || { color: 'bg-gray-100 text-gray-700 border-gray-200', label: stage || '—' };
  return <span data-testid={`stage-pill-${stage}`} className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wide border ${s.color}`}>{s.label}</span>;
};

const EA_DEFAULT_SUBJECT = 'Enrollment Agreement — please sign to secure your seat';
const EA_DEFAULT_BODY = (student, college) => `Dear ${student},

Congratulations on your admission to ${college}! Attached is your Enrollment Agreement. Please review it carefully and sign electronically via the DocuSign link in the accompanying email.

Once we receive your signed agreement we will proceed to issue your Confirmation of Enrollment (COE).

If you have any questions, please reply to this email or contact our admissions team.

Warm regards,
${college} Admissions`;

const COE_DEFAULT_SUBJECT = 'Confirmation of Enrollment (COE) — welcome to the program';
const COE_DEFAULT_BODY = (student, college) => `Dear ${student},

Thank you for signing the Enrollment Agreement. Attached is your official Confirmation of Enrollment (COE).

Please review it and reach out if any details need to be updated.

We look forward to welcoming you to ${college}.

Best regards,
${college} Admissions`;

export default function InstitutionDashboard() {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [students, setStudents] = useState([]);
  const [collegeNames, setCollegeNames] = useState([]);
  const [counts, setCounts] = useState({});
  const [lastVisitAt, setLastVisitAt] = useState('');
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [stageFilter, setStageFilter] = useState('all');
  // Accuplacer-pending filter: students who need the in-house placement
  // test — no English test proof, no 2-yr Canadian transcript, intent=Yes.
  const [showAccuplacerPending, setShowAccuplacerPending] = useState(false);
  const [intakeFilter, setIntakeFilter] = useState('all');
  const [programFilter, setProgramFilter] = useState('all');
  const [recruiterFilter, setRecruiterFilter] = useState('all');
  const [flagsFilter, setFlagsFilter] = useState('all');       // 'all' | 'flagged' | 'clean' | 'new'
  const [forwardedFilter, setForwardedFilter] = useState('all'); // 'all' | '7d' | '30d' | '90d'
  const [sortBy, setSortBy] = useState('forwarded_desc');
  const clearFilters = () => {
    setSearch(''); setStageFilter('all'); setIntakeFilter('all');
    setProgramFilter('all'); setRecruiterFilter('all');
    setFlagsFilter('all'); setForwardedFilter('all'); setSortBy('forwarded_desc');
    setShowAccuplacerPending(false);
  };
  const [selected, setSelected] = useState(null);
  const [selectedFull, setSelectedFull] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  // Multi-select for the Selective Export → Student ID Report workbook
  const [selectedIds, setSelectedIds] = useState([]);
  const [exporting, setExporting] = useState(false);
  // Bulk cohort ZIP export (registrar hands the whole intake to compliance).
  // We poll a background job so the request doesn't hit Cloudflare's 100s
  // edge timeout on large batches.
  const [zipJob, setZipJob] = useState(null); // { job_id, filename, total, state, done, current_student, current_index }
  const [zipBusy, setZipBusy] = useState(false);
  // Quarterly Report (27-column April template) — same admin flow, scoped
  // to the registrar's colleges + visible stages. Runs on the same job
  // pattern as ZIP: kick-off → poll → download via signed token.
  const [qrJob, setQrJob] = useState(null);
  const [qrBusy, setQrBusy] = useState(false);
  // "BULK EA (Via Excel Sheet)" — upload the college's approved list,
  // auto-match by name + email, then fire EAs to the matched students
  // (compliance gate bypassed because the college has manually vetted).
  const [bulkEaSheetOpen, setBulkEaSheetOpen] = useState(false);
  const [flagOpen, setFlagOpen] = useState(false);
  const [flagDocType, setFlagDocType] = useState('');
  const [flagNote, setFlagNote] = useState('');
  const [busy, setBusy] = useState(false);

  // Financials modal state (institution 1/2 Money Received flow + admin edit).
  const [financialsOpen, setFinancialsOpen] = useState(false);
  const [financialsMode, setFinancialsMode] = useState('edit');

  const [eaOpen, setEaOpen] = useState(false);
  const [prepareEaOpen, setPrepareEaOpen] = useState(false);
  const [bulkEaOpen, setBulkEaOpen] = useState(false);
  const [bulkEaMode, setBulkEaMode] = useState('prepare'); // 'check' | 'prepare'
  const [eaSubject, setEaSubject] = useState('');
  const [eaBody, setEaBody] = useState('');
  const [eaFile, setEaFile] = useState(null);

  const [coeOpen, setCoeOpen] = useState(false);
  const [coeSubject, setCoeSubject] = useState('');
  const [coeBody, setCoeBody] = useState('');
  const [coeFile, setCoeFile] = useState(null);

  // Document preview modal state
  const [previewFile, setPreviewFile] = useState(null);

  // Institution comment composer state
  const [commentDraft, setCommentDraft] = useState('');

  // Card grid vs compact list — persisted per user in localStorage
  const [viewMode, setViewMode] = useState(() => {
    try { return localStorage.getItem('institutionViewMode') || 'cards'; } catch { return 'cards'; }
  });
  // Top-level tab: 'students' (the current dashboard) or 'manual-sync' (bulk uploader).
  const [topTab, setTopTab] = useState('students');
  const [instGroupByIntakeRaw, setInstGroupByIntakeRaw] = useGroupByIntake('institution.students.groupByIntake', true);
  // 'staff' (default) sees the full pipeline; 'president' only sees students
  // from EA Signed onwards and controls COE Sent + 1/2 Money Received.
  const institutionRole = (user?.institution_role || 'staff').toLowerCase();
  const isPresident = institutionRole === 'president';
  const isStaff = !isPresident;
  // Staff should never see a flat list — force grouping ON, ignore any
  // stale localStorage value that a previous click may have flipped off.
  const instGroupByIntake = isStaff ? true : instGroupByIntakeRaw;
  const setInstGroupByIntake = setInstGroupByIntakeRaw;
  const setView = (m) => {
    setViewMode(m);
    try { localStorage.setItem('institutionViewMode', m); } catch { /* ignore */ }
  };

  // Bulk EA/COE upload state — one dialog handles both target stages.
  const [showCoeUpload, setShowCoeUpload] = useState(false);
  const [coeFileToUpload, setCoeFileToUpload] = useState(null);
  const [coeUploading, setCoeUploading] = useState(false);
  const [coeResult, setCoeResult] = useState(null);
  const [bulkTargetStage, setBulkTargetStage] = useState('coe_sent');

  const submitCoeUpload = async () => {
    if (!coeFileToUpload) { toast.error('Please choose a file first.'); return; }
    setCoeUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', coeFileToUpload);
      fd.append('stage', bulkTargetStage);
      const { data } = await axios.post(`${API}/institution/students/sync-coe-file`, fd);
      setCoeResult(data);
      const label = bulkTargetStage === 'enrollment_agreement_signed' ? 'EA Signed' : 'COE Sent';
      toast.success(`Updated ${data.updated} student${data.updated === 1 ? '' : 's'} to ${label}`);
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Upload failed');
    } finally { setCoeUploading(false); }
  };

  useEffect(() => {
    if (user && user.role !== 'institution') {
      navigate(user.role === 'admin' ? '/admin' : '/recruiter', { replace: true });
    }
  }, [user, navigate]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/institution/students`);
      setStudents(data.items || []);
      setCounts(data.counts || {});
      setLastVisitAt(data.last_visit_at || '');
      setCollegeNames(data.college_names || (data.college_name ? [data.college_name] : []));
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to load students');
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  // Auto-refresh every 30s so new students from recruiters appear instantly.
  useEffect(() => {
    const id = setInterval(() => { load(); }, 30_000);
    return () => clearInterval(id);
  }, [load]);

  // The "since last visit" stamp is bumped only when the user explicitly
  // acknowledges via the banner button, or when they log out. NOT on every
  // dashboard mount — otherwise refreshing the page silently wipes the
  // "new since last login" indicator the user is asking to see.
  const [showOnlyNew, setShowOnlyNew] = useState(false);
  const markAllSeen = async () => {
    try {
      await axios.post(`${API}/institution/mark-visited`);
      toast.success('Marked new students as seen');
      // Local optimistic clear so the UI updates without another fetch.
      setStudents(prev => prev.map(s => ({ ...s, is_new: false })));
      setCounts(prev => ({ ...prev, _new: 0 }));
      setShowOnlyNew(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to mark seen');
    }
  };

  const openDetail = async (s) => {
    setSelected(s);
    setSelectedFull(null);
    setDetailLoading(true);
    try {
      const { data } = await axios.get(`${API}/institution/students/${s.id}`);
      setSelectedFull(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to load profile');
      setSelected(null);
    } finally { setDetailLoading(false); }
  };

  const refreshSelected = async () => {
    if (!selected) return;
    await load();
    try {
      const { data } = await axios.get(`${API}/institution/students/${selected.id}`);
      setSelectedFull(data);
    } catch { /* ignore */ }
  };

  // ── Selective Student ID Report export ─────────────────────────────
  const toggleSelectId = (id) => {
    setSelectedIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };
  // Toggle every filtered row on/off in one shot (used by the header checkbox).
  const toggleSelectAllFiltered = (filteredList) => {
    const ids = (filteredList || []).map(s => s.id);
    const allSelected = ids.length > 0 && ids.every(id => selectedIds.includes(id));
    if (allSelected) {
      setSelectedIds(prev => prev.filter(id => !ids.includes(id)));
    } else {
      setSelectedIds(prev => Array.from(new Set([...prev, ...ids])));
    }
  };
  const clearSelection = () => setSelectedIds([]);
  const handleExportSelected = async () => {
    if (!selectedIds.length) return;
    setExporting(true);
    try {
      const res = await axios.post(
        `${API}/institution/students/export.xlsx`,
        { student_ids: selectedIds },
        { responseType: 'blob' },
      );
      const blob = new Blob([res.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const stamp = new Date().toISOString().slice(0, 19).replace(/[:T]/g, '-');
      a.download = `Institution_StudentIDReport_${stamp}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success(`Exported ${selectedIds.length} student${selectedIds.length === 1 ? '' : 's'}`);
      setSelectedIds([]);
    } catch (err) {
      // Blob error handling — parse as text
      let msg = 'Export failed';
      try {
        const txt = err?.response?.data;
        if (txt instanceof Blob) {
          const parsed = JSON.parse(await txt.text());
          msg = parsed.detail || msg;
        } else if (typeof txt === 'string') {
          msg = txt;
        } else if (txt?.detail) {
          msg = txt.detail;
        }
      } catch { /* ignore */ }
      toast.error(msg);
    } finally {
      setExporting(false);
    }
  };

  // Kick off a bulk cohort ZIP for the selected students. Backend queues a
  // background packaging job; we poll every 1.5s and then trigger a native
  // browser download via a signed query token (bypasses axios blob buffering
  // which chokes on 100+ MB archives).
  const handleDownloadZipSelected = async () => {
    if (!selectedIds.length) return;
    if (selectedIds.length > 500) {
      toast.error('Please select 500 students or fewer per batch');
      return;
    }
    setZipBusy(true);
    try {
      const res = await axios.post(
        `${API}/institution/students/bulk-download-zip`,
        { student_ids: selectedIds },
      );
      const job = res.data;
      setZipJob({ ...job, done: 0 });
      toast.info(`Packaging ${job.total} student${job.total === 1 ? '' : 's'}…`);

      // Poll until done or failed (with 15 min ceiling — matches admin flow).
      const deadline = Date.now() + 15 * 60 * 1000;
      let finished = null;
      while (Date.now() < deadline) {
        await new Promise(r => setTimeout(r, 1500));
        try {
          const s = await axios.get(`${API}/institution/students/bulk-download-zip-jobs/${job.job_id}`);
          setZipJob({ ...job, ...s.data });
          if (s.data.state === 'done') { finished = s.data; break; }
          if (s.data.state === 'failed') {
            toast.error(`Packaging failed: ${s.data.error || 'unknown error'}`);
            setZipJob(null);
            return;
          }
        } catch (pollErr) {
          // Transient poll errors are fine — keep trying.
        }
      }
      if (!finished) {
        toast.error('Packaging timed out — please try a smaller batch');
        setZipJob(null);
        return;
      }
      // Trigger native browser download via the signed token.
      const url = `${API}/institution/students/bulk-download-zip-jobs/${job.job_id}/download?t=${encodeURIComponent(finished.download_token)}`;
      const a = document.createElement('a');
      a.href = url;
      a.download = job.filename || 'cohort.zip';
      document.body.appendChild(a);
      a.click();
      a.remove();
      toast.success(`Downloaded ${job.total} student${job.total === 1 ? '' : 's'}`);
      setZipJob(null);
      setSelectedIds([]);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to start bulk download');
      setZipJob(null);
    } finally {
      setZipBusy(false);
    }
  };

  // Kick off Quarterly Report Format XLSX for the selected students.
  // Same job-polling shape as the ZIP flow above (Cloudflare 100s edge
  // timeout guard). The admin flow lives at /admin/... — we hit the
  // /institution/... twin so students outside the registrar's colleges
  // get filtered server-side.
  const handleQuarterlyReportSelected = async () => {
    if (!selectedIds.length) return;
    if (selectedIds.length > 500) {
      toast.error('Please select 500 students or fewer per batch');
      return;
    }
    setQrBusy(true);
    try {
      const res = await axios.post(
        `${API}/institution/students/quarterly-report-jobs`,
        { ids: selectedIds, ocr_missing: true },
      );
      const job = res.data;
      setQrJob({ ...job, done: 0 });
      toast.info(`Building Quarterly Report for ${job.total} student${job.total === 1 ? '' : 's'}…`);

      const deadline = Date.now() + 15 * 60 * 1000;
      let finished = null;
      while (Date.now() < deadline) {
        await new Promise(r => setTimeout(r, 1500));
        try {
          const s = await axios.get(`${API}/institution/students/quarterly-report-jobs/${job.job_id}`);
          setQrJob({ ...job, ...s.data });
          if (s.data.state === 'done') { finished = s.data; break; }
          if (s.data.state === 'failed') {
            toast.error(`Report failed: ${s.data.error || 'unknown error'}`);
            setQrJob(null);
            return;
          }
        } catch (pollErr) {
          // Transient poll errors — keep trying until deadline.
        }
      }
      if (!finished) {
        toast.error('Report timed out — please try a smaller batch');
        setQrJob(null);
        return;
      }
      const url = `${API}/institution/students/quarterly-report-jobs/${job.job_id}/download?t=${encodeURIComponent(finished.download_token)}`;
      const a = document.createElement('a');
      a.href = url;
      a.download = job.filename || 'quarterly_report.xlsx';
      document.body.appendChild(a);
      a.click();
      a.remove();
      toast.success(`Downloaded Quarterly Report (${job.total})`);
      setQrJob(null);
      setSelectedIds([]);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to build Quarterly Report');
      setQrJob(null);
    } finally {
      setQrBusy(false);
    }
  };


  // ---- Actions ----
  const submitComment = async () => {
    if (!selected || !commentDraft.trim()) return;
    setBusy(true);
    try {
      await axios.post(`${API}/institution/students/${selected.id}/comment`, { comment: commentDraft.trim() });
      setCommentDraft('');
      toast.success('Note sent — recruiter has been notified');
      await refreshSelected();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to post note');
    } finally { setBusy(false); }
  };

  const doStartReview = async () => {
    if (!selected) return;
    setBusy(true);
    try {
      await axios.post(`${API}/institution/students/${selected.id}/mark-review`);
      toast.success('Marked "Under Review"');
      await refreshSelected();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to start review');
    } finally { setBusy(false); }
  };
  const doVerify = async () => {
    if (!selected) return;
    setBusy(true);
    try {
      await axios.post(`${API}/institution/students/${selected.id}/verify-docs`);
      toast.success('All docs verified — student can now receive an Enrollment Agreement');
      await refreshSelected();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to verify');
    } finally { setBusy(false); }
  };
  const doFlagMissing = async () => {
    if (!selected || !flagDocType.trim()) return;
    setBusy(true);
    try {
      await axios.post(`${API}/institution/students/${selected.id}/flag-missing-doc`, {
        doc_type: flagDocType.trim(),
        note: flagNote.trim(),
      });
      toast.success('Recruiter notified — awaiting their re-upload');
      setFlagOpen(false); setFlagDocType(''); setFlagNote('');
      await refreshSelected();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to flag');
    } finally { setBusy(false); }
  };

  const openEa = () => {
    const stuCollege = selectedFull?.assigned_college_name || collegeNames[0] || 'Your college';
    setEaSubject(EA_DEFAULT_SUBJECT);
    setEaBody(EA_DEFAULT_BODY(selectedFull?.full_name || 'Student', stuCollege));
    setEaFile(null);
    setEaOpen(true);
  };
  const sendEa = async () => {
    if (!selected || !eaFile) { toast.error('Please attach the EA PDF'); return; }
    setBusy(true);
    const fd = new FormData();
    fd.append('subject', eaSubject);
    fd.append('body', eaBody);
    fd.append('pdf', eaFile);
    try {
      await axios.post(`${API}/institution/students/${selected.id}/send-ea`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success('EA sent — student will receive a DocuSign email');
      setEaOpen(false);
      await refreshSelected();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to send EA');
    } finally { setBusy(false); }
  };

  const openCoe = () => {
    const stuCollege = selectedFull?.assigned_college_name || collegeNames[0] || 'Your college';
    setCoeSubject(COE_DEFAULT_SUBJECT);
    setCoeBody(COE_DEFAULT_BODY(selectedFull?.full_name || 'Student', stuCollege));
    setCoeFile(null);
    setCoeOpen(true);
  };
  const sendCoe = async () => {
    if (!selected || !coeFile) { toast.error('Please attach the COE PDF'); return; }
    setBusy(true);
    const fd = new FormData();
    fd.append('subject', coeSubject);
    fd.append('body', coeBody);
    fd.append('pdf', coeFile);
    try {
      await axios.post(`${API}/institution/students/${selected.id}/send-coe`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success('COE sent!');
      setCoeOpen(false);
      await refreshSelected();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to send COE');
    } finally { setBusy(false); }
  };

  // Normalise the many ways intake can be stored. Prefer the human label
  // (`intake_label` like "Jul 2026") so the filter dropdown shows every
  // unique intake actually present in the data — not just month buckets.
  const _extractIntakeLabel = (s) => {
    const raw = ((s.intake_label || s.preferred_intake || '') + '').trim();
    if (raw) return raw;
    if (s.intake_date) {
      const d = new Date(s.intake_date);
      if (!Number.isNaN(d.getTime())) {
        const m = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'][d.getUTCMonth()];
        return `${m} ${d.getUTCFullYear()}`;
      }
    }
    return '';
  };

  // A student counts as "has English proof" when EITHER the onboarding
  // Q&A boolean is true OR an English-test-proof document has been
  // uploaded. Mirrors the backend auto-reconcile on upload. Same pattern
  // for the 2-yr Canadian diploma / transcript.
  const _hasEnglishProof = (s) => (
    s.english_test_proof === true
    || (Array.isArray(s.documents) && s.documents.some(d => d?.doc_type === 'english_test_proof'))
  );
  const _hasCanadian2yr = (s) => (
    s.canadian_transcript_2yr === true
    || (Array.isArray(s.documents) && s.documents.some(d => d?.doc_type === 'canadian_2yr_diploma' || d?.doc_type === 'canadian_transcript_2yr'))
  );
  const isAccuplacerPending = (s) => (
    !_hasEnglishProof(s)
    && !_hasCanadian2yr(s)
    && s.accuplacer_intent === true
  );

  const filtered = useMemo(() => {
    const q = search.toLowerCase().trim();
    const now = Date.now();
    const withinWindow = (iso, days) => {
      if (!iso || !days) return true;
      const t = new Date(iso).getTime();
      if (!Number.isFinite(t)) return true;
      return (now - t) <= days * 24 * 60 * 60 * 1000;
    };
    const daysMap = { '7d': 7, '30d': 30, '90d': 90 };
    const out = students.filter(s => {
      if (showOnlyNew && !s.is_new) return false;
      if (stageFilter !== 'all' && s.application_stage !== stageFilter) return false;
      if (intakeFilter !== 'all' && _extractIntakeLabel(s) !== intakeFilter) return false;
      if (programFilter !== 'all') {
        const p = s.assigned_program_name || s.preferred_program_name || s.preferred_program || '';
        if (p !== programFilter) return false;
      }
      if (recruiterFilter !== 'all' && (s.recruiter_name || '—') !== recruiterFilter) return false;
      if (flagsFilter === 'flagged' && !(s.open_missing_docs > 0)) return false;
      if (flagsFilter === 'clean' && (s.open_missing_docs || 0) > 0) return false;
      if (flagsFilter === 'new' && !s.is_new) return false;
      if (forwardedFilter !== 'all' && !withinWindow(s.last_forwarded_at || s.created_at, daysMap[forwardedFilter])) return false;
      if (showAccuplacerPending && !isAccuplacerPending(s)) return false;
      if (!q) return true;
      return (s.full_name || '').toLowerCase().includes(q)
        || (s.email || '').toLowerCase().includes(q)
        || (s.recruiter_name || '').toLowerCase().includes(q);
    });
    // Sort
    const byDateDesc = (a, b) => ((b.last_forwarded_at || b.created_at || '')).localeCompare(a.last_forwarded_at || a.created_at || '');
    const byDateAsc = (a, b) => -byDateDesc(a, b);
    const byName = (a, b) => (a.full_name || '').localeCompare(b.full_name || '');
    const byStage = (a, b) => (a.application_stage || '').localeCompare(b.application_stage || '');
    const cmp = { forwarded_desc: byDateDesc, forwarded_asc: byDateAsc, name_asc: byName, name_desc: (a, b) => -byName(a, b), stage: byStage }[sortBy] || byDateDesc;
    return out.slice().sort(cmp);
  }, [students, search, stageFilter, intakeFilter, programFilter, recruiterFilter, flagsFilter, forwardedFilter, sortBy, showOnlyNew, showAccuplacerPending]);

  // How many students fit the Accuplacer-pending pattern — total pool,
  // ignoring the other filters, so the chip badge is always accurate.
  const accuplacerPendingCount = useMemo(() => (
    students.filter(isAccuplacerPending).length
  ), [students]);

  // Unique programs & recruiters for filter dropdowns (built from the loaded set).
  const availablePrograms = useMemo(() => {
    const s = new Set();
    students.forEach(x => {
      const p = x.assigned_program_name || x.preferred_program_name || x.preferred_program || '';
      if (p) s.add(p);
    });
    return Array.from(s).sort();
  }, [students]);
  const availableRecruiters = useMemo(() => {
    const s = new Set();
    students.forEach(x => { if (x.recruiter_name) s.add(x.recruiter_name); });
    return Array.from(s).sort();
  }, [students]);

  const anyFilterActive = search.trim() || stageFilter !== 'all' || intakeFilter !== 'all'
    || programFilter !== 'all' || recruiterFilter !== 'all' || flagsFilter !== 'all'
    || forwardedFilter !== 'all' || sortBy !== 'forwarded_desc' || showAccuplacerPending;

  // KPI counts derived from the currently-visible (intake-filtered) set so
  // switching intakes updates the tiles instantly.
  const derivedCounts = useMemo(() => {
    const c = { _new: 0, _open_flags: 0 };
    // Only the intake filter should shape KPI counts — search/stage filters
    // are user-driven refinements that shouldn't hide the pipeline picture.
    const base = intakeFilter === 'all'
      ? students
      : students.filter(s => _extractIntakeLabel(s) === intakeFilter);
    base.forEach(s => {
      const k = s.application_stage || '';
      c[k] = (c[k] || 0) + 1;
      if (s.is_new) c._new += 1;
      c._open_flags += (s.open_missing_docs || 0);
    });
    return c;
  }, [students, intakeFilter]);

  // Compute available intakes for the dropdown from the actual data. Keep
  // them sorted chronologically (parse "MMM YYYY" back to a date for sort).
  const availableIntakes = useMemo(() => {
    const set = new Set();
    students.forEach(s => { const label = _extractIntakeLabel(s); if (label) set.add(label); });
    const monthIdx = { Jan:0, Feb:1, Mar:2, Apr:3, May:4, Jun:5, Jul:6, Aug:7, Sep:8, Oct:9, Nov:10, Dec:11 };
    return Array.from(set).sort((a, b) => {
      const [ma, ya] = a.split(/\s+/);
      const [mb, yb] = b.split(/\s+/);
      const ka = (parseInt(ya, 10) || 0) * 12 + (monthIdx[(ma || '').slice(0, 3)] || 0);
      const kb = (parseInt(yb, 10) || 0) * 12 + (monthIdx[(mb || '').slice(0, 3)] || 0);
      return ka - kb;
    });
  }, [students]);

  const stage = selectedFull?.application_stage || selected?.application_stage;
  const openMissing = selectedFull?.missing_docs?.filter(m => !m.resolved).length || 0;
  // Role-aware permission gates. Staff drives docs → EA; President drives
  // COE Sent → 1/2 Money Received. Admin flows aren't handled here (admin
  // has its own dashboard).
  const canStartReview = !isPresident && ['profile_created', 'application_sent'].includes(stage);
  const canVerify = !isPresident && ['profile_created', 'application_sent', 'docs_under_review'].includes(stage) && openMissing === 0;
  const canFlag = !isPresident && ['profile_created', 'application_sent', 'docs_under_review'].includes(stage);
  const canSendEa = !isPresident && stage === 'docs_verified';
  const canMarkEaSigned = !isPresident && stage === 'enrollment_agreement_sent';
  // President-only actions: plain COE Sent (no popup) + 1/2 & 2/2 Money Received.
  const canMarkCoeSent = isPresident && stage === 'enrollment_agreement_signed';
  // Only offer "Mark 1/2 Money Received" BEFORE the president has done it.
  // Previously `half_money_received` was included here which caused the
  // button to keep showing after they'd already advanced — confusing UX
  // that made staff think the click hadn't registered.
  const canMarkHalfMoney = isPresident && ['enrollment_agreement_signed', 'coe_sent'].includes(stage);
  // Same rule for the 2/2 tranche — only show the button while it can
  // still be legitimately clicked (student is at 1/2 but not yet 2/2).
  const canMarkSecondMoney = isPresident && stage === 'half_money_received';
  // Retire the old file-attach popup for institution users; COE is now a
  // status-only flip driven by `mark-coe-sent`.
  const canSendCoe = false;

  const doMarkEaSigned = async () => {
    if (!selected) return;
    setBusy(true);
    try {
      await axios.post(`${API}/institution/students/${selected.id}/mark-ea-signed`);
      toast.success('EA marked as signed');
      await load();
      await openDetail(selected);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to mark EA signed');
    } finally { setBusy(false); }
  };

  // "Mark COE Sent" is now routed through FinancialsModal in
  // `advance-coe-sent` mode — the modal handles the POST + stage flip so we
  // can capture the payment breakdown at the same moment the COE is issued.

  return (
    <div className="min-h-screen bg-[#f8f7f4] text-gray-900" data-testid="institution-dashboard">
      {/* Editorial header — no gradient */}
      <header className="border-b border-gray-200 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-10">
          <div className="flex items-start justify-between gap-6 flex-col lg:flex-row">
            <div className="min-w-0">
              <p className="text-[10px] font-semibold uppercase tracking-[0.22em] text-gray-400">
                {isPresident ? 'College President Portal' : 'College Staff Portal'}
              </p>
              <h1 className="text-4xl lg:text-5xl font-semibold tracking-tight text-gray-900 mt-2 leading-[1.05]" style={{ fontFamily: "'Libre Baskerville', serif" }}>
                {collegeNames.length > 0 ? collegeNames.join(' · ') : 'Welcome'}
              </h1>
              <p className="text-[12px] text-gray-500 mt-3">
                Signed in as <span className="text-gray-700">{user?.email}</span>
                <span className="mx-2 text-gray-300">·</span>
                <span className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-[0.14em] text-gray-700" data-testid="institution-role-badge">
                  <span aria-hidden className={`inline-block w-1.5 h-1.5 rounded-full ${isPresident ? 'bg-gray-900' : 'bg-gray-400'}`} />
                  {isPresident ? 'President' : 'Staff'}
                </span>
              </p>
            </div>
            <div className="flex items-center gap-2 shrink-0 flex-wrap">
              <NotificationBell basePath="/institution" />
              {(counts._new > 0) && (
                <span className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md bg-gray-900 text-white text-[11px] font-semibold" data-testid="new-arrivals-badge">
                  <span className="w-1.5 h-1.5 rounded-full bg-white/80" />
                  {counts._new} new since last visit
                </span>
              )}
              <Button variant="outline" size="sm" onClick={load} disabled={loading} className="h-10 border-gray-200 text-gray-700 hover:bg-gray-100 rounded-md text-[12px] font-medium bg-white" data-testid="institution-refresh-btn">
                <ArrowClockwise size={13} className={`mr-1.5 ${loading ? 'animate-spin' : ''}`} /> Refresh
              </Button>
              <Button variant="outline" size="sm" onClick={logout} className="h-10 border-gray-200 text-gray-700 hover:bg-gray-100 rounded-md text-[12px] font-medium bg-white" data-testid="institution-logout-btn">
                <SignOut size={13} className="mr-1.5" /> Logout
              </Button>
            </div>
          </div>

          {/* KPI strip — flat hairline row. Post-EA-signed KPIs are gated
              to the President only (staff cannot see tranche/financial data). */}
          <div className="mt-10 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 lg:grid-cols-10 divide-x divide-gray-200 border-t border-b border-gray-200">
            {[
              { key: '_new',                          label: 'New Arrivals',   icon: Sparkle, presidentOnly: false },
              { key: 'profile_created',               label: 'Awaiting Review',icon: Clock,   presidentOnly: false },
              { key: 'application_sent',              label: 'Application Sent',icon: FileText, presidentOnly: false },
              { key: 'docs_under_review',             label: 'Under Review',   icon: ArrowClockwise, presidentOnly: false },
              { key: 'docs_verified',                 label: 'Docs Verified',  icon: CheckCircle, presidentOnly: false },
              { key: 'enrollment_agreement_sent',     label: 'EA Sent',        icon: PaperPlaneTilt, presidentOnly: false },
              { key: 'enrollment_agreement_signed',   label: 'EA Signed',      icon: CheckCircle, presidentOnly: false },
              { key: 'coe_sent',                      label: 'COE Sent',       icon: PaperPlaneTilt, presidentOnly: true },
              { key: 'half_money_received',           label: '1/2 Money',      icon: CheckCircle, presidentOnly: true },
              { key: 'second_money_received',         label: '2/2 Money',      icon: CheckCircle, presidentOnly: true },
            ].filter(k => isPresident || !k.presidentOnly).map((k, i) => {
              const Icon = k.icon;
              const val = derivedCounts[k.key] || 0;
              const emphasis = k.key === '_new' && val > 0;
              return (
                <div key={k.key} className={`py-5 px-3 ${i === 0 ? 'pl-0' : ''}`} data-testid={`kpi-${k.key}`}>
                  <div className="flex items-center gap-1.5 mb-2">
                    <Icon size={11} className={emphasis ? 'text-gray-900' : 'text-gray-400'} />
                    <p className={`text-[9px] font-semibold uppercase tracking-[0.14em] ${emphasis ? 'text-gray-900' : 'text-gray-500'}`}>{k.label}</p>
                  </div>
                  <p className="text-2xl font-semibold text-gray-900 tabular-nums tracking-tight leading-none" style={{ fontFamily: "'Libre Baskerville', serif" }}>{val}</p>
                </div>
              );
            })}
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 lg:px-12 py-10 space-y-8">
        {/* "New since last visit" banner */}
        {counts._new > 0 && topTab === 'students' && (
          <div className="relative border-l-2 border-gray-900 bg-white px-5 py-4 flex flex-wrap items-center gap-3" data-testid="new-since-last-visit-banner">
            <div className="flex-1 min-w-[200px]">
              <p className="text-[13px] font-semibold text-gray-900">
                {counts._new} new student{counts._new === 1 ? '' : 's'} since your last visit
              </p>
              {lastVisitAt && <p className="text-[11px] text-gray-500 mt-0.5">Last visit: {new Date(lastVisitAt).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' })}</p>}
            </div>
            <div className="flex items-center gap-2">
              <Button size="sm" variant="outline" onClick={() => setShowOnlyNew(v => !v)} className="border-gray-200 text-gray-700 hover:bg-gray-100 rounded-md text-[12px] font-medium bg-white h-9" data-testid="banner-show-only-new">
                {showOnlyNew ? 'Show all' : 'Show only new'}
              </Button>
              <Button size="sm" onClick={markAllSeen} className="bg-gray-900 hover:bg-black text-white rounded-md h-9 text-[12px] font-medium px-4" data-testid="banner-mark-all-seen">
                Mark all as seen
              </Button>
            </div>
          </div>
        )}

        {/* Top-level tab strip */}
        <div className="inline-flex rounded-md border border-gray-200 bg-white overflow-hidden" data-testid="institution-top-tabs">
          <button
            type="button"
            onClick={() => setTopTab('students')}
            className={`px-5 py-2 text-[12px] font-semibold transition-colors ${topTab === 'students' ? 'bg-gray-900 text-white' : 'text-gray-600 hover:bg-gray-50'}`}
            data-testid="institution-top-tab-students"
          >Students</button>
          <button
            type="button"
            onClick={() => setTopTab('manual-sync')}
            className={`px-5 py-2 text-[12px] font-semibold transition-colors border-l border-gray-200 ${topTab === 'manual-sync' ? 'bg-gray-900 text-white' : 'text-gray-600 hover:bg-gray-50'}`}
            data-testid="institution-top-tab-manual-sync"
          >Manual Sync</button>
          <button
            type="button"
            onClick={() => setTopTab('signed-eas')}
            className={`px-5 py-2 text-[12px] font-semibold transition-colors border-l border-gray-200 ${topTab === 'signed-eas' ? 'bg-gray-900 text-white' : 'text-gray-600 hover:bg-gray-50'}`}
            data-testid="institution-top-tab-signed-eas"
          >Signed EAs</button>
          {isPresident && (
            <button
              type="button"
              onClick={() => setTopTab('pre-auth')}
              className={`px-5 py-2 text-[12px] font-semibold transition-colors border-l border-gray-200 ${topTab === 'pre-auth' ? 'bg-gray-900 text-white' : 'text-gray-600 hover:bg-gray-50'}`}
              data-testid="institution-top-tab-pre-auth"
            >Pre-Auth</button>
          )}
        </div>

        {topTab === 'manual-sync' ? (
          <ManualSyncTab onAnyUploadDone={load} />
        ) : topTab === 'signed-eas' ? (
          <SignedEAsTab />
        ) : topTab === 'pre-auth' ? (
          <InstitutionPreAuthorizationTab />
        ) : (
        <>
        {/* Search + filter */}
        <div className="flex flex-col gap-4 pb-6 border-b border-gray-200">
          <div className="flex flex-wrap items-center gap-2">
            <div className="relative flex-1 min-w-[240px] max-w-md">
              <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
              <Input
                placeholder="Search by name, email or recruiter…"
                className="pl-9 bg-white border-gray-200 h-10 text-[13px]"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                data-testid="institution-search-input"
              />
            </div>
            <select
              className="border border-gray-200 rounded-md px-3 py-2 text-[13px] bg-white h-10 min-w-[160px]"
              value={stageFilter}
              onChange={(e) => setStageFilter(e.target.value)}
              data-testid="institution-stage-filter"
            >
              <option value="all">All stages ({filtered.length})</option>
              {Object.entries(STAGE_STYLES)
                .filter(([k]) => isPresident || !['coe_sent', 'half_money_received', 'second_money_received'].includes(k))
                .map(([k, v]) => (
                  <option key={k} value={k}>{v.label} ({derivedCounts[k] || 0})</option>
                ))}
            </select>
            <select
              className="border border-gray-200 rounded-md px-3 py-2 text-[13px] bg-white h-10 min-w-[130px]"
              value={intakeFilter}
              onChange={(e) => setIntakeFilter(e.target.value)}
              data-testid="institution-intake-filter"
              title="Filter by intake month"
            >
              <option value="all">All intakes</option>
              {availableIntakes.map(label => (
                <option key={label} value={label}>{label} intake</option>
              ))}
            </select>
            <select
              className="border border-gray-200 rounded-md px-3 py-2 text-[13px] bg-white h-10 max-w-[220px] truncate"
              value={programFilter}
              onChange={(e) => setProgramFilter(e.target.value)}
              data-testid="institution-program-filter"
              title="Filter by program"
            >
              <option value="all">All programs</option>
              {availablePrograms.map(p => <option key={p} value={p}>{p}</option>)}
            </select>
            <select
              className="border border-gray-200 rounded-md px-3 py-2 text-[13px] bg-white h-10 max-w-[180px] truncate"
              value={recruiterFilter}
              onChange={(e) => setRecruiterFilter(e.target.value)}
              data-testid="institution-recruiter-filter"
              title="Filter by recruiter"
            >
              <option value="all">All recruiters</option>
              {availableRecruiters.map(r => <option key={r} value={r}>{r}</option>)}
            </select>
            <select
              className="border border-gray-200 rounded-md px-3 py-2 text-[13px] bg-white h-10"
              value={flagsFilter}
              onChange={(e) => setFlagsFilter(e.target.value)}
              data-testid="institution-flags-filter"
              title="Flags"
            >
              <option value="all">All rows</option>
              <option value="new">New arrivals only</option>
              <option value="flagged">Has missing docs</option>
              <option value="clean">No missing docs</option>
            </select>
            <select
              className="border border-gray-200 rounded-md px-3 py-2 text-[13px] bg-white h-10"
              value={forwardedFilter}
              onChange={(e) => setForwardedFilter(e.target.value)}
              data-testid="institution-forwarded-filter"
              title="Forwarded within…"
            >
              <option value="all">All time</option>
              <option value="7d">Last 7 days</option>
              <option value="30d">Last 30 days</option>
              <option value="90d">Last 90 days</option>
            </select>
            <select
              className="border border-gray-200 rounded-md px-3 py-2 text-[13px] bg-white h-10"
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              data-testid="institution-sort-by"
              title="Sort by"
            >
              <option value="forwarded_desc">Newest first</option>
              <option value="forwarded_asc">Oldest first</option>
              <option value="name_asc">Name A → Z</option>
              <option value="name_desc">Name Z → A</option>
              <option value="stage">Stage</option>
            </select>
            {accuplacerPendingCount > 0 && (
              <button
                type="button"
                onClick={() => setShowAccuplacerPending(v => !v)}
                className={`inline-flex items-center gap-1.5 h-10 px-3 text-[12px] font-medium rounded-md border transition ${showAccuplacerPending ? 'bg-purple-600 text-white border-purple-600 shadow-sm' : 'bg-white text-purple-700 border-purple-300 hover:bg-purple-50'}`}
                data-testid="institution-toggle-accuplacer-pending"
                title="Students who need to take the Accuplacer: no English test proof, no 2-yr Canadian transcript, and intent=Yes"
              >
                <GraduationCap size={13} weight={showAccuplacerPending ? 'fill' : 'regular'} />
                Accuplacer pending
                <span className={`tabular-nums font-semibold px-1.5 py-0.5 rounded ${showAccuplacerPending ? 'bg-white/20 text-white' : 'bg-purple-100 text-purple-800'}`}>
                  {accuplacerPendingCount}
                </span>
              </button>
            )}
            {anyFilterActive && (
              <Button variant="ghost" size="sm" onClick={clearFilters} className="h-10 text-gray-500 hover:text-gray-900 text-[12px]" data-testid="institution-clear-filters-btn">
                Clear filters
              </Button>
            )}
          </div>
          <div className="flex flex-wrap items-center gap-2 justify-between">
            {lastVisitAt ? (
              <p className="text-[12px] text-gray-500 hidden sm:block">
                Last visit: <span className="tabular-nums text-gray-700">{new Date(lastVisitAt).toLocaleString()}</span>
                <span className="mx-2 text-gray-300">·</span>
                Showing <span className="tabular-nums font-semibold text-gray-900">{filtered.length}</span> of <span className="tabular-nums">{students.length}</span>
              </p>
            ) : <span />}
            <div className="flex items-center gap-2">
              {!isStaff && (
                <GroupByIntakeToggle
                  value={instGroupByIntake}
                  onChange={setInstGroupByIntake}
                  storageKey="institution.students.groupByIntake"
                  testId="institution-group-by-intake-toggle"
                />
              )}
              <div className="inline-flex rounded-md border border-gray-200 bg-white overflow-hidden h-10" data-testid="view-toggle">
                <button
                  type="button"
                  onClick={() => setView('cards')}
                  className={`px-3 h-full inline-flex items-center gap-1.5 text-[11px] font-semibold transition-colors ${viewMode === 'cards' ? 'bg-gray-900 text-white' : 'text-gray-500 hover:text-gray-900'}`}
                  title="Card view"
                  data-testid="view-toggle-cards"
                >
                  <SquaresFour size={12} weight={viewMode === 'cards' ? 'fill' : 'regular'} /> Cards
                </button>
                <button
                  type="button"
                  onClick={() => setView('list')}
                  className={`px-3 h-full inline-flex items-center gap-1.5 text-[11px] font-semibold transition-colors border-l border-gray-200 ${viewMode === 'list' ? 'bg-gray-900 text-white' : 'text-gray-500 hover:text-gray-900'}`}
                  title="List view"
                  data-testid="view-toggle-list"
                >
                  <ListBullets size={12} weight={viewMode === 'list' ? 'fill' : 'regular'} /> List
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Selective Export bar */}
        {selectedIds.length > 0 && (
          <div className="bg-white border border-gray-200 border-l-2 border-l-gray-900 px-5 py-3 flex flex-wrap items-center gap-3 justify-between" data-testid="institution-selection-bar">
            <div className="flex items-center gap-3">
              <span className="inline-flex items-center gap-2 text-[13px] font-semibold text-gray-900" data-testid="institution-selection-count">
                <DownloadSimple size={14} weight="bold" /> {selectedIds.length} student{selectedIds.length === 1 ? '' : 's'} selected
              </span>
              <button
                type="button"
                onClick={clearSelection}
                className="text-[11px] text-gray-500 hover:text-gray-900 underline decoration-dotted underline-offset-2"
                data-testid="institution-clear-selection-btn"
              >
                Clear
              </button>
            </div>
            <div className="flex items-center gap-2">
              <Button
                size="sm"
                onClick={() => { setBulkEaMode('check'); setBulkEaOpen(true); }}
                disabled={exporting || zipBusy || qrBusy}
                className="bg-blue-700 hover:bg-blue-800 text-white h-9 rounded-md text-[12px] font-medium px-4"
                data-testid="institution-bulk-doc-check-btn"
                title="Run the AI compliance audit on every selected student — no EA is drafted or sent."
              >
                <MagnifyingGlass size={13} className="mr-1.5" weight="bold" /> Document Check ({selectedIds.length})
              </Button>
              <Button
                size="sm"
                onClick={() => { setBulkEaMode('prepare'); setBulkEaOpen(true); }}
                disabled={exporting || zipBusy || qrBusy}
                className="bg-emerald-700 hover:bg-emerald-800 text-white h-9 rounded-md text-[12px] font-medium px-4"
                data-testid="institution-bulk-prepare-ea-btn"
                title="Prepare an Enrollment Agreement for every selected student — AI classifies uploaded docs, staff reviews and ticks the rows to send"
              >
                <Sparkle size={13} className="mr-1.5" weight="fill" /> Prepare EA ({selectedIds.length})
              </Button>
              <Button
                size="sm"
                onClick={handleExportSelected}
                disabled={exporting || zipBusy || qrBusy}
                className="bg-gray-900 hover:bg-black text-white h-9 rounded-md text-[12px] font-medium px-4"
                data-testid="institution-export-selected-btn"
                title="Download the 28-column Student ID Report workbook for the selected students only"
              >
                {exporting ? <><CircleNotch size={13} className="mr-1.5 animate-spin" /> Exporting…</> : <><DownloadSimple size={13} className="mr-1.5" /> Export Student ID Report</>}
              </Button>
              <Button
                size="sm"
                onClick={handleQuarterlyReportSelected}
                disabled={exporting || zipBusy || qrBusy}
                className="bg-purple-700 hover:bg-purple-800 text-white h-9 rounded-md text-[12px] font-medium px-4"
                data-testid="institution-quarterly-report-btn"
                title="Build the 27-column Quarterly Report Format workbook (April template) for the selected students — runs GPT-4o Vision OCR on IDs to fill any missing address fields"
              >
                {qrBusy ? (
                  <><CircleNotch size={13} className="mr-1.5 animate-spin" />
                    {qrJob
                      ? `Building ${qrJob.done || 0}/${qrJob.total || selectedIds.length}…`
                      : 'Starting…'}
                  </>
                ) : (
                  <><DownloadSimple size={13} className="mr-1.5" /> Quarterly Report Format ({selectedIds.length})</>
                )}
              </Button>
              <Button
                size="sm"
                onClick={handleDownloadZipSelected}
                disabled={exporting || zipBusy || qrBusy}
                className="bg-indigo-700 hover:bg-indigo-800 text-white h-9 rounded-md text-[12px] font-medium px-4"
                data-testid="institution-download-zip-btn"
                title="Package every selected student's profile PDF + uploaded documents into one ZIP, with one folder per student"
              >
                {zipBusy ? (
                  <><CircleNotch size={13} className="mr-1.5 animate-spin" />
                    {zipJob
                      ? `Packaging ${zipJob.current_index || 0}/${zipJob.total || selectedIds.length}…`
                      : 'Starting…'}
                  </>
                ) : (
                  <><DownloadSimple size={13} className="mr-1.5" /> Download as ZIP ({selectedIds.length})</>
                )}
              </Button>
            </div>
          </div>
        )}

        {/* ── Student cards ────────────────────────────────────────── */}
        <div className="space-y-3">
          {loading && filtered.length === 0 && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
              {[1, 2, 3, 4].map(i => (
                <div key={i} className="bg-white rounded-xl border border-slate-200 h-24 animate-pulse" />
              ))}
            </div>
          )}
          {!loading && filtered.length === 0 && (
            <div className="bg-white rounded-2xl border border-slate-200 p-14 text-center shadow-sm">
              <div className="w-14 h-14 mx-auto rounded-2xl bg-indigo-50 border border-indigo-100 flex items-center justify-center mb-3">
                <GraduationCap size={22} weight="fill" className="text-indigo-500" />
              </div>
              <p className="text-sm font-semibold text-slate-800">No students visible yet</p>
              <p className="text-xs text-slate-500 mt-1 max-w-md mx-auto">
                Students land here the moment a recruiter forwards their application to {collegeNames.length > 0 ? <strong>{collegeNames.join(' or ')}</strong> : 'your college'}. New arrivals will appear with a green highlight.
              </p>
            </div>
          )}
          {viewMode === 'cards' && (() => {
            const renderCardsGrid = (items) => (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                {items.map(s => {
                  const stageMeta = STAGE_STYLES[s.application_stage] || { color: 'bg-gray-100 text-gray-700 border-gray-200', label: s.application_stage || '—' };
                  const isNew = !!s.is_new;
                  const isFlagged = (s.open_missing_docs || 0) > 0;
                  const isChecked = selectedIds.includes(s.id);
                  return (
                    <div
                      key={s.id}
                      className={`group relative w-full text-left rounded-xl border shadow-sm transition-all hover:-translate-y-0.5 hover:shadow-md overflow-hidden ${isChecked ? 'bg-indigo-50 border-indigo-300 ring-2 ring-indigo-200' : isNew ? 'bg-gradient-to-br from-emerald-50 to-white border-emerald-300' : isFlagged ? 'bg-gradient-to-br from-amber-50/60 to-white border-amber-200' : 'bg-white border-slate-200 hover:border-indigo-200'}`}
                      data-testid={`institution-row-${s.id}`}
                    >
                      {/* Selection checkbox — top-left, stops the card-click open */}
                      <div className="absolute top-3 left-3 z-10" onClick={(e) => e.stopPropagation()}>
                        <Checkbox
                          checked={isChecked}
                          onCheckedChange={() => toggleSelectId(s.id)}
                          data-testid={`institution-select-${s.id}`}
                          aria-label={`Select ${s.full_name || 'student'}`}
                          className="bg-white shadow-sm border-slate-300"
                        />
                      </div>
                      <button
                        onClick={() => openDetail(s)}
                        type="button"
                        className="w-full text-left"
                      >
                      {/* Left accent bar */}
                      <span className={`absolute left-0 top-0 bottom-0 w-1 ${isChecked ? 'bg-indigo-500' : isNew ? 'bg-emerald-500' : isFlagged ? 'bg-amber-500' : 'bg-slate-200 group-hover:bg-indigo-400'} transition-colors`} />
                      {/* NEW ribbon */}
                      {isNew && (
                        <span className="absolute top-3 right-3 inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider rounded-full bg-emerald-500 text-white shadow" data-testid={`new-ribbon-${s.id}`}>
                          <span className="w-1.5 h-1.5 rounded-full bg-white animate-pulse" /> New
                        </span>
                      )}
                      <div className="p-4 pl-12">
                        <div className="flex items-start justify-between gap-3 mb-2">
                          <div className="min-w-0 flex-1">
                            <p className="font-semibold text-slate-900 truncate">{s.full_name || '—'}</p>
                            <p className="text-xs text-slate-500 truncate">{s.email || ''}</p>
                          </div>
                        </div>
                        <div className="flex items-center flex-wrap gap-2 mt-1">
                          <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wide border ${stageMeta.color}`}>
                            {stageMeta.label}
                          </span>
                          {isFlagged && (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-semibold rounded-full bg-amber-100 text-amber-800 border border-amber-300">
                              <WarningCircle size={10} weight="fill" /> {s.open_missing_docs} missing
                            </span>
                          )}
                        </div>
                        <div className="grid grid-cols-2 gap-2 mt-3 text-[11px] text-slate-600">
                          <div className="truncate">
                            <span className="text-slate-400 uppercase tracking-wider text-[9px] block">Program</span>
                            <span className="truncate">{s.assigned_program_name || s.preferred_program_name || s.preferred_program || '—'}</span>
                          </div>
                          <div className="truncate">
                            <span className="text-slate-400 uppercase tracking-wider text-[9px] block">Recruiter</span>
                            <span className="truncate">{s.recruiter_name || '—'}</span>
                          </div>
                          <div className="truncate">
                            <span className="text-slate-400 uppercase tracking-wider text-[9px] block">College</span>
                            <span className="truncate">{s.assigned_college_name || '—'}</span>
                          </div>
                          <div>
                            <span className="text-slate-400 uppercase tracking-wider text-[9px] block">Forwarded</span>
                            <span>{s.last_forwarded_at ? new Date(s.last_forwarded_at).toLocaleDateString() : '—'}</span>
                          </div>
                        </div>
                      </div>
                      </button>
                    </div>
                  );
                })}
              </div>
            );
            return instGroupByIntake ? (
              <IntakeAccordion
                students={filtered}
                stages={Object.keys(STAGE_STYLES)}
                stageMeta={(k) => ({ label: STAGE_STYLES[k]?.label || k, dot: STAGE_STYLES[k]?.dot || 'bg-gray-400', className: STAGE_STYLES[k]?.color || 'bg-gray-100 text-gray-700 border-gray-200' })}
                storageKey="institution.students.openIntakes"
                emptyMessage="No students match your filters."
                renderGroupExtra={(groupItems) => {
                  const groupIds = groupItems.map(s => s.id);
                  const allChecked = groupIds.length > 0 && groupIds.every(id => selectedIds.includes(id));
                  const someChecked = !allChecked && groupIds.some(id => selectedIds.includes(id));
                  const intakeLabel = groupItems[0]?.intake_label || 'none';
                  return (
                    <div className="inline-flex items-center gap-2 flex-wrap justify-end">
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); setBulkEaSheetOpen(true); }}
                        className="inline-flex items-center gap-1.5 text-[11px] font-semibold text-white bg-gray-900 hover:bg-black border border-gray-900 rounded-md px-2.5 py-1 cursor-pointer transition-colors shadow-sm"
                        title={`Upload the college's approved student list (CSV/XLSX) for the ${intakeLabel} intake and auto-send EAs to every matched student`}
                        data-testid={`intake-bulk-ea-sheet-${intakeLabel}`}
                      >
                        <FileText size={11} weight="bold" />
                        BULK EA (Via Excel Sheet)
                      </button>
                      <label
                        onClick={(e) => e.stopPropagation()}
                        className="inline-flex items-center gap-1.5 text-[11px] font-medium text-slate-700 bg-white/80 hover:bg-white border border-slate-300 rounded-md px-2 py-1 cursor-pointer transition-colors shadow-sm"
                        title={allChecked ? 'Unselect every student in this intake' : 'Select every student in this intake'}
                        data-testid={`intake-select-all-${intakeLabel}`}
                      >
                        <input
                          type="checkbox"
                          className="w-3.5 h-3.5 rounded border-slate-400"
                          checked={allChecked}
                          ref={(el) => { if (el) el.indeterminate = someChecked; }}
                          onChange={() => {
                            setSelectedIds(prev => allChecked
                              ? prev.filter(id => !groupIds.includes(id))
                              : Array.from(new Set([...prev, ...groupIds])));
                          }}
                        />
                        {allChecked ? 'All selected' : (someChecked ? `${groupIds.filter(id => selectedIds.includes(id)).length}/${groupIds.length} selected` : 'Select all in intake')}
                      </label>
                    </div>
                  );
                }}
                renderGroup={(items) => (
                  <div className="p-3 sm:p-4">
                    {renderCardsGrid(items)}
                  </div>
                )}
              />
            ) : renderCardsGrid(filtered);
          })()}

          {viewMode === 'list' && filtered.length > 0 && (() => {
            const renderListCard = (items) => {
              const allChecked = items.length > 0 && items.every(s => selectedIds.includes(s.id));
              return (
              <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden" data-testid="institution-list-view">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-50/70 border-b border-slate-200">
                      <tr>
                        <th className="w-10 px-3 py-3">
                          <Checkbox
                            checked={allChecked}
                            onCheckedChange={() => toggleSelectAllFiltered(items)}
                            data-testid="institution-select-all"
                            aria-label="Select all visible students"
                          />
                        </th>
                        <th className="text-left px-5 py-3 text-[10px] font-bold text-slate-500 uppercase tracking-[0.08em]">Student</th>
                        <th className="text-left px-4 py-3 text-[10px] font-bold text-slate-500 uppercase tracking-[0.08em]">Stage</th>
                        <th className="text-left px-4 py-3 text-[10px] font-bold text-slate-500 uppercase tracking-[0.08em]">Program</th>
                        <th className="text-left px-4 py-3 text-[10px] font-bold text-slate-500 uppercase tracking-[0.08em] hidden md:table-cell">Recruiter</th>
                        <th className="text-left px-4 py-3 text-[10px] font-bold text-slate-500 uppercase tracking-[0.08em] hidden lg:table-cell">College</th>
                        <th className="text-left px-4 py-3 text-[10px] font-bold text-slate-500 uppercase tracking-[0.08em] hidden md:table-cell whitespace-nowrap">Forwarded</th>
                        <th className="text-right px-5 py-3 text-[10px] font-bold text-slate-500 uppercase tracking-[0.08em]">Flags</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {items.map(s => {
                        const stageMeta = STAGE_STYLES[s.application_stage] || { color: 'bg-gray-100 text-gray-700 border-gray-200', label: s.application_stage || '—' };
                        const isNew = !!s.is_new;
                        const isFlagged = (s.open_missing_docs || 0) > 0;
                        const isChecked = selectedIds.includes(s.id);
                        return (
                          <tr
                            key={s.id}
                            onClick={() => openDetail(s)}
                            className={`cursor-pointer transition-colors ${isChecked ? 'bg-indigo-50/80 hover:bg-indigo-50' : isNew ? 'bg-emerald-50/50 hover:bg-emerald-50' : isFlagged ? 'bg-amber-50/40 hover:bg-amber-50' : 'hover:bg-indigo-50/40'}`}
                            data-testid={`institution-row-${s.id}`}
                          >
                            <td className="px-3 py-3" onClick={(e) => e.stopPropagation()}>
                              <Checkbox
                                checked={isChecked}
                                onCheckedChange={() => toggleSelectId(s.id)}
                                data-testid={`institution-select-${s.id}`}
                                aria-label={`Select ${s.full_name || 'student'}`}
                              />
                            </td>
                            <td className="px-5 py-3">
                              <div className="flex items-center gap-2">
                                {isNew && <span className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider rounded-full bg-emerald-500 text-white" data-testid={`new-ribbon-${s.id}`}><span className="w-1 h-1 rounded-full bg-white animate-pulse" />New</span>}
                                <div className="min-w-0">
                                  <p className="text-sm font-semibold text-slate-900 truncate">{s.full_name || '—'}</p>
                                  <p className="text-[11px] text-slate-500 truncate">{s.email || ''}</p>
                                </div>
                              </div>
                            </td>
                            <td className="px-4 py-3">
                              <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wide border ${stageMeta.color}`}>{stageMeta.label}</span>
                            </td>
                            <td className="px-4 py-3 text-[12px] text-slate-700 max-w-[220px] truncate">
                              {s.assigned_program_name || s.preferred_program_name || s.preferred_program || '—'}
                            </td>
                            <td className="px-4 py-3 text-[12px] text-slate-700 hidden md:table-cell max-w-[160px] truncate">{s.recruiter_name || '—'}</td>
                            <td className="px-4 py-3 text-[12px] text-slate-700 hidden lg:table-cell max-w-[180px] truncate">{s.assigned_college_name || '—'}</td>
                            <td className="px-4 py-3 text-[11px] text-slate-500 hidden md:table-cell whitespace-nowrap">{s.last_forwarded_at ? new Date(s.last_forwarded_at).toLocaleDateString() : '—'}</td>
                            <td className="px-5 py-3 text-right">
                              {isFlagged && (
                                <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-semibold rounded-full bg-amber-100 text-amber-800 border border-amber-300">
                                  <WarningCircle size={10} weight="fill" /> {s.open_missing_docs}
                                </span>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
              );
            };
            return instGroupByIntake ? (
              <IntakeAccordion
                students={filtered}
                stages={Object.keys(STAGE_STYLES)}
                stageMeta={(k) => ({ label: STAGE_STYLES[k]?.label || k, dot: STAGE_STYLES[k]?.dot || 'bg-gray-400', className: STAGE_STYLES[k]?.color || 'bg-gray-100 text-gray-700 border-gray-200' })}
                storageKey="institution.students.openIntakes"
                emptyMessage="No students match your filters."
                renderGroup={(items) => renderListCard(items)}
              />
            ) : renderListCard(filtered);
          })()}
        </div>
        </>
        )}
      </main>

      {/* Student detail Sheet */}
      <Sheet open={!!selected} onOpenChange={(o) => { if (!o) { setSelected(null); setSelectedFull(null); } }}>
        <SheetContent className="w-full sm:max-w-2xl overflow-y-auto" data-testid="institution-detail-sheet">
          <SheetHeader>
            <SheetTitle>{selected?.full_name || 'Student'}</SheetTitle>
          </SheetHeader>
          {detailLoading && <p className="text-slate-500 mt-6">Loading profile…</p>}
          {selectedFull && (
            <div className="mt-5 space-y-6">
              {/* Header info */}
              <div className="flex items-center gap-3">
                <StagePill stage={stage} />
                {openMissing > 0 && (
                  <Badge className="bg-amber-100 text-amber-800 border-amber-300 border">
                    <WarningCircle size={12} className="mr-1" /> {openMissing} open flag{openMissing > 1 ? 's' : ''}
                  </Badge>
                )}
                {selectedFull.ea_status === 'signed' && (
                  <Badge className="bg-emerald-100 text-emerald-800 border-emerald-300 border">EA signed ✓</Badge>
                )}
                {/* Download — enabled whenever ANY source exists (manual upload OR either DocuSign field) */}
                {(selectedFull.ea_signed_pdf_filename || selectedFull.ea_docusign_envelope_id || selectedFull.ea_envelope_id) && (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={async () => {
                      try {
                        const resp = await axios.get(
                          `${API}/institution/students/${selectedFull.id}/ea-signed-pdf`,
                          { responseType: 'blob' },
                        );
                        const url = window.URL.createObjectURL(new Blob([resp.data], { type: 'application/pdf' }));
                        const a = document.createElement('a');
                        a.href = url;
                        a.download = `EA_signed_${(selectedFull.full_name || 'student').replace(/[^a-zA-Z0-9._-]/g, '_')}.pdf`;
                        document.body.appendChild(a);
                        a.click();
                        a.remove();
                        window.URL.revokeObjectURL(url);
                      } catch (err) {
                        let detail = '';
                        const body = err?.response?.data;
                        if (body instanceof Blob) {
                          try {
                            const text = await body.text();
                            try { detail = (JSON.parse(text) || {}).detail || text; } catch { detail = text; }
                          } catch { /* ignore */ }
                        } else if (body?.detail) {
                          detail = body.detail;
                        }
                        toast.error(detail || 'Failed to download signed EA');
                      }
                    }}
                    className="h-7 px-2.5 text-[11px] font-medium border-emerald-300 text-emerald-800 hover:bg-emerald-50 rounded-md"
                    data-testid={`download-signed-ea-${selectedFull.id}`}
                    title={selectedFull.ea_signed_pdf_filename ? 'Download the manually-uploaded signed EA PDF' : 'Download the signed Enrollment Agreement PDF from DocuSign'}
                  >
                    <DownloadSimple size={12} className="mr-1" /> Download signed EA
                  </Button>
                )}
                {/* Upload — appears when ea_status is signed OR stage ≥ enrollment_agreement_signed
                    AND no manual-upload file is on record yet. Lets registrar attach a hand-signed
                    PDF for students who went through bulk-upload (no DocuSign envelope). */}
                {!selectedFull.ea_signed_pdf_filename && (selectedFull.ea_status === 'signed' || ['enrollment_agreement_signed', 'coe_sent', 'half_money_received', 'second_money_received'].includes(stage)) && (
                  <label
                    className="inline-flex items-center gap-1 h-7 px-2.5 text-[11px] font-medium border border-indigo-300 text-indigo-800 hover:bg-indigo-50 rounded-md cursor-pointer transition"
                    data-testid={`upload-signed-ea-${selectedFull.id}`}
                    title="Attach the signed PDF for this student (if signed outside DocuSign or on paper)"
                  >
                    <UploadSimple size={12} />
                    {busy ? 'Uploading…' : 'Upload signed EA'}
                    <input
                      type="file"
                      accept="application/pdf"
                      className="hidden"
                      disabled={busy}
                      onChange={async (e) => {
                        const f = e.target.files?.[0];
                        if (!f) return;
                        e.target.value = '';
                        setBusy(true);
                        try {
                          const form = new FormData();
                          form.append('file', f);
                          await axios.post(
                            `${API}/institution/students/${selectedFull.id}/ea-signed-pdf-upload`,
                            form,
                            { headers: { 'Content-Type': 'multipart/form-data' } },
                          );
                          toast.success('Signed EA uploaded — now downloadable');
                          await refreshSelected?.();
                        } catch (err) {
                          toast.error(err?.response?.data?.detail || 'Upload failed');
                        } finally {
                          setBusy(false);
                        }
                      }}
                    />
                  </label>
                )}
              </div>

              {/* Actions */}
              <div className="flex flex-wrap gap-2">
                {canStartReview && (
                  <Button onClick={doStartReview} disabled={busy} className="bg-amber-500 hover:bg-amber-600 text-white" data-testid="btn-start-review">
                    {busy ? <CircleNotch className="animate-spin mr-2" size={14} /> : null} Start Review
                  </Button>
                )}
                {canVerify && (
                  <Button onClick={doVerify} disabled={busy} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="btn-verify-docs">
                    {busy ? <CircleNotch className="animate-spin mr-2" size={14} /> : <CheckCircle size={14} className="mr-2" />} Verify All Docs
                  </Button>
                )}
                {canFlag && (
                  <Button onClick={() => setFlagOpen(true)} disabled={busy} variant="outline" className="border-amber-300 text-amber-800" data-testid="btn-flag-missing">
                    <WarningCircle size={14} className="mr-2" /> Flag Missing Doc
                  </Button>
                )}
                {canSendEa && (
                  <>
                    <Button onClick={() => setPrepareEaOpen(true)} disabled={busy} className="bg-emerald-700 hover:bg-emerald-800 text-white" data-testid="btn-prepare-ea">
                      <Sparkle size={14} className="mr-2" weight="fill" /> Prepare EA (Autofill)
                    </Button>
                    <Button onClick={openEa} disabled={busy} variant="outline" className="border-gray-300" data-testid="btn-send-ea">
                      <PaperPlaneTilt size={14} className="mr-2" /> Upload PDF manually
                    </Button>
                  </>
                )}
                {canMarkEaSigned && (
                  <Button onClick={doMarkEaSigned} disabled={busy} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="btn-mark-ea-signed">
                    <CheckCircle size={14} className="mr-2" /> Mark EA Signed
                  </Button>
                )}
                {canMarkCoeSent && (
                  <Button onClick={() => { setFinancialsMode('advance-coe-sent'); setFinancialsOpen(true); }} disabled={busy} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="btn-mark-coe-sent">
                    <CheckCircle size={14} className="mr-2" /> Mark COE Sent
                  </Button>
                )}
                {canMarkHalfMoney && (
                  <Button onClick={() => { setFinancialsMode('advance-half-money'); setFinancialsOpen(true); }} disabled={busy} className="bg-lime-600 hover:bg-lime-700 text-white" data-testid="btn-mark-half-money">
                    <CurrencyDollar size={14} className="mr-2" /> Mark 1/2 Money Received
                  </Button>
                )}
                {canMarkSecondMoney && (
                  <Button onClick={() => { setFinancialsMode('advance-second-money'); setFinancialsOpen(true); }} disabled={busy} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="btn-mark-second-money">
                    <CurrencyDollar size={14} className="mr-2" /> Mark 2/2 Money Received
                  </Button>
                )}
                {canSendCoe && (
                  <Button onClick={openCoe} disabled={busy} className="bg-indigo-600 hover:bg-indigo-700 text-white" data-testid="btn-send-coe">
                    <PaperPlaneTilt size={14} className="mr-2" /> Send COE
                  </Button>
                )}
                {stage === 'enrollment_agreement_sent' && !isPresident && (
                  <Button
                    onClick={async () => {
                      try {
                        setBusy(true);
                        const r = await axios.post(
                          `${API}/institution/students/${selected.id}/ea/sync-status`,
                          {},
                        );
                        if (r.data?.advanced) {
                          toast.success('EA signed — student advanced');
                          await refreshSelected();
                        } else {
                          toast.info(`DocuSign status: ${r.data?.status || 'unknown'} — no change yet`);
                        }
                      } catch (e) {
                        toast.error(e?.response?.data?.detail || 'Sync failed');
                      } finally {
                        setBusy(false);
                      }
                    }}
                    disabled={busy}
                    variant="outline"
                    className="border-indigo-300 text-indigo-700 bg-indigo-50 hover:bg-indigo-100"
                    data-testid="btn-sync-ea-status"
                  >
                    <ArrowsClockwise size={14} className="mr-2" /> {busy ? 'Checking…' : 'Awaiting student signature (tap to sync)'}
                  </Button>
                )}
                {stage === 'enrollment_agreement_sent' && !isPresident && (
                  <Button
                    onClick={async () => {
                      if (!window.confirm('Resend this EA to the student? A new DocuSign envelope will be created using the same prepared data.')) return;
                      try {
                        setBusy(true);
                        const r = await axios.post(
                          `${API}/institution/students/${selected.id}/ea/resend`,
                          {},
                        );
                        toast.success(`EA resent · envelope ${r.data?.envelope_id?.slice(0, 8) || ''}`);
                        setStudents(prev => prev.map(s => s.id === selected?.id ? { ...s, ea_envelope_id: r.data?.envelope_id } : s));
                        setSelected(prev => prev ? { ...prev, ea_envelope_id: r.data?.envelope_id } : prev);
                        await refreshSelected();
                      } catch (e) {
                        toast.error(e?.response?.data?.detail || 'Resend failed');
                      } finally {
                        setBusy(false);
                      }
                    }}
                    disabled={busy}
                    variant="outline"
                    className="border-emerald-300 text-emerald-700 bg-emerald-50 hover:bg-emerald-100"
                    data-testid="btn-resend-ea"
                    title="Create a fresh DocuSign envelope using the previously prepared EA data."
                  >
                    <PaperPlaneTilt size={14} className="mr-2" /> Resend EA
                  </Button>
                )}
              </div>

              {/* Profile */}
              <section>
                <h3 className="text-sm font-semibold text-slate-900 mb-2">Profile</h3>
                <div className="grid grid-cols-2 gap-3">
                  {(() => {
                    const yn = (v) => v === true ? 'Yes' : v === false ? 'No' : '';
                    const line1 = selectedFull.address_line1 || selectedFull.address_line1_extracted;
                    const line2 = selectedFull.address_line2 || selectedFull.address_line2_extracted;
                    const city = selectedFull.city || selectedFull.city_extracted;
                    const province = selectedFull.province || selectedFull.province_extracted;
                    const cityProv = [city, province].filter(Boolean).join(' ') || '—';
                    const progStart = selectedFull.intake_date || selectedFull.assigned_intake_start;
                    const progEnd = selectedFull.assigned_intake_end;
                    const isMature = selectedFull.is_mature_student;
                    return [
                      ['Full Name', selectedFull.full_name],
                      ['Email', selectedFull.email],
                      ['Phone', `${selectedFull.phone_code || ''} ${selectedFull.phone || ''}`.trim()],
                      ['DOB', selectedFull.dob],
                      ['Gender', selectedFull.gender],
                      ['Nationality', selectedFull.nationality],
                      ['Passport / PR', selectedFull.passport_pr_status],
                      ['Marital Status', selectedFull.marital_status],
                      ['Mature Student', yn(isMature)],
                      ...(isMature === true
                        ? [['2-yr Canadian Diploma', yn(selectedFull.has_canadian_2yr_diploma)]]
                        : [
                            ['English Test Proof', yn(selectedFull.english_test_proof)],
                            ['2-yr Canadian Transcript', yn(selectedFull.canadian_transcript_2yr)],
                            ['Accuplacer Intent', yn(selectedFull.accuplacer_intent)],
                          ]),
                      ['Program (assigned)', selectedFull.assigned_program_name || selectedFull.preferred_program_name || selectedFull.preferred_program],
                      ['Program (typed)', selectedFull.preferred_program],
                      ['Intake (typed)', selectedFull.preferred_intake || selectedFull.intake_label],
                      ['Assigned Intake', selectedFull.intake_label || '—'],
                      ['Program Start', progStart || '—'],
                      ['Program End', progEnd || '—'],
                      ['Recruiter', selectedFull.recruiter_name],
                      ['Campus / Branch', selectedFull.assigned_branch || selectedFull.preferred_branch || '—'],
                      ['Address', selectedFull.address, true],
                      ['Address Line 1', line1 || '—'],
                      ['Address Line 2', line2 || '—'],
                      ['City / Province', cityProv],
                      ['Postal Code', selectedFull.postal_code || selectedFull.postal_code_extracted],
                      ['Country', selectedFull.country_extracted || 'Canada'],
                      ['Needs Exam Prep', selectedFull.needs_exam_prep ? (selectedFull.exam_prep_types?.length ? selectedFull.exam_prep_types.join(', ') : 'Yes') : 'No'],
                      ['Academic History', selectedFull.academic_history, true],
                      ['Notes', selectedFull.notes, true],
                      ['Profile Created', selectedFull.created_at ? new Date(selectedFull.created_at).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' }) : '—'],
                      ['Last Updated', selectedFull.updated_at ? new Date(selectedFull.updated_at).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' }) : '—'],
                    ];
                  })().map(([k, v, wide]) => (
                    <div key={k} className={wide ? 'col-span-2' : ''} data-testid={`inst-profile-field-${String(k).replace(/[^a-zA-Z0-9]+/g, '-').toLowerCase()}`}>
                      <p className="text-[10px] uppercase tracking-wider text-slate-500 font-medium">{k}</p>
                      <p className="text-sm text-slate-900 whitespace-pre-line break-words">{v || '—'}</p>
                    </div>
                  ))}
                </div>
              </section>

              {/* Financials (institution PRESIDENT + admin only — staff and recruiters must never see this section) */}
              {!isStaff && (
                <section>
                  <h3 className="text-sm font-semibold text-slate-900 mb-2 flex items-center gap-1.5">
                    <CurrencyDollar size={14} className="text-emerald-600" /> Financials
                  </h3>
                  <FinancialsSummary
                    studentId={selectedFull.id}
                    canEdit
                    onEditClick={() => { setFinancialsMode('edit'); setFinancialsOpen(true); }}
                  />
                </section>
              )}

              {/* Score Reports — intentionally not rendered as a dedicated
                  section. Generated Accuplacer PDFs land in the Documents
                  list below (doc_type: english_test_proof) so staff see
                  them alongside every other uploaded file. */}

              {/* Pipeline Timeline */}
              {(selectedFull.pipeline_history || []).length > 0 && (
                <section>
                  <h3 className="text-sm font-semibold text-slate-900 mb-3 flex items-center gap-1.5">
                    <Clock size={14} className="text-slate-500" /> Pipeline Timeline
                  </h3>
                  <ol className="relative border-l-2 border-slate-200 ml-1.5 space-y-3" data-testid="pipeline-timeline">
                    {[...(selectedFull.pipeline_history || [])].reverse().map((h, idx) => {
                      const meta = STAGE_STYLES[h.to_stage] || { color: 'bg-gray-100 text-gray-700 border-gray-200', label: h.to_stage };
                      return (
                        <li key={idx} className="pl-4 relative">
                          <span className={`absolute -left-[7px] top-1.5 w-3 h-3 rounded-full border-2 border-white ${idx === 0 ? 'bg-emerald-500 ring-2 ring-emerald-200' : 'bg-slate-300'}`} />
                          <div className="flex items-start justify-between gap-2 flex-wrap">
                            <div>
                              <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wide border ${meta.color}`}>{meta.label}</span>
                              <p className="text-xs text-slate-600 mt-1">
                                by {h.changed_by || 'system'}
                              </p>
                              {h.note && <p className="text-xs text-slate-500 mt-1 italic">&ldquo;{h.note}&rdquo;</p>}
                            </div>
                            <span className="text-[10px] text-slate-400 whitespace-nowrap">
                              {h.changed_at ? new Date(h.changed_at).toLocaleString() : ''}
                            </span>
                          </div>
                        </li>
                      );
                    })}
                  </ol>
                </section>
              )}

              {/* Documents with inline previews */}
              <section>
                <h3 className="text-sm font-semibold text-slate-900 mb-2">Documents ({selectedFull.files?.length || 0})</h3>
                {(!selectedFull.files || selectedFull.files.length === 0) && (
                  <p className="text-sm text-slate-500">No documents uploaded yet.</p>
                )}
                <div className="grid grid-cols-2 gap-2" data-testid="documents-grid">
                  {(selectedFull.files || []).map(f => {
                    const url = `${API}/files/${f.id}/download`;
                    const mime = (f.mime_type || f.content_type || '').toLowerCase();
                    const isImage = mime.startsWith('image/') || /\.(jpe?g|png|gif|webp|heic|heif)$/i.test(f.original_filename || '');
                    const isPdf = mime.includes('pdf') || /\.pdf$/i.test(f.original_filename || '');
                    return (
                      <div key={f.id} className="rounded-lg border border-slate-200 bg-white overflow-hidden hover:shadow-md hover:border-indigo-200 transition-all group">
                        <button
                          type="button"
                          onClick={() => setPreviewFile({ ...f, url, isImage, isPdf })}
                          className="w-full aspect-[4/3] bg-slate-50 flex items-center justify-center overflow-hidden relative"
                          data-testid={`file-preview-btn-${f.id}`}
                        >
                          {isImage ? (
                            <img src={url} alt={f.original_filename || 'document'} className="w-full h-full object-cover group-hover:scale-105 transition-transform" loading="lazy" />
                          ) : isPdf ? (
                            <div className="flex flex-col items-center gap-1 text-rose-500">
                              <FilePdf size={32} weight="fill" />
                              <span className="text-[10px] font-semibold uppercase tracking-wider">PDF</span>
                            </div>
                          ) : (
                            <div className="flex flex-col items-center gap-1 text-slate-400">
                              <FileText size={32} />
                              <span className="text-[10px] font-semibold uppercase tracking-wider">FILE</span>
                            </div>
                          )}
                          <span className="absolute top-2 right-2 inline-flex items-center gap-1 px-2 py-0.5 text-[9px] font-semibold uppercase tracking-wider rounded-full bg-white/95 text-slate-700 border border-slate-200 opacity-0 group-hover:opacity-100 transition-opacity">
                            <Eye size={10} weight="fill" /> Preview
                          </span>
                        </button>
                        <div className="p-2 border-t border-slate-100">
                          <p className="text-[11px] font-semibold text-slate-900 truncate" title={f.original_filename}>{f.original_filename || f.doc_type}</p>
                          <div className="flex items-center justify-between mt-0.5">
                            <p className="text-[9px] text-slate-500 truncate">{f.doc_type}</p>
                            <a href={url} target="_blank" rel="noopener noreferrer" className="text-[10px] text-indigo-600 hover:underline shrink-0 ml-1" data-testid={`institution-file-${f.id}`} onClick={(e) => e.stopPropagation()}>
                              Open
                            </a>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </section>

              {/* Institution Comments — thread + composer */}
              <section data-testid="institution-comments-section">
                <h3 className="text-sm font-semibold text-slate-900 mb-2 flex items-center gap-1.5">
                  <ChatCircleDots size={14} className="text-slate-500" /> Notes to Recruiter ({(selectedFull.institution_comments || []).length})
                </h3>
                <p className="text-[11px] text-slate-500 mb-2 -mt-1">Your recruiter gets an instant bell notification + email each time you leave a note here.</p>
                <div className="space-y-2 mb-3 max-h-72 overflow-y-auto">
                  {(selectedFull.institution_comments || []).slice().reverse().map(c => (
                    <div key={c.id} className="p-3 rounded-lg border border-indigo-200 bg-indigo-50/60" data-testid={`comment-${c.id}`}>
                      <p className="text-sm text-slate-800 whitespace-pre-wrap break-words">{c.comment}</p>
                      <p className="text-[10px] text-slate-500 mt-1.5">
                        {c.author_name || c.author_email} · {c.college_name ? `${c.college_name} · ` : ''}{c.created_at ? new Date(c.created_at).toLocaleString() : ''}
                      </p>
                    </div>
                  ))}
                  {(!selectedFull.institution_comments || selectedFull.institution_comments.length === 0) && (
                    <p className="text-xs text-slate-400 italic">No notes yet. Leave the first one below.</p>
                  )}
                </div>
                <div className="space-y-2">
                  <Textarea
                    rows={3}
                    placeholder="e.g. Grade 12 transcript is unreadable — please re-upload a clearer scan…"
                    value={commentDraft}
                    onChange={(e) => setCommentDraft(e.target.value)}
                    data-testid="comment-input"
                  />
                  <div className="flex justify-end">
                    <Button size="sm" onClick={submitComment} disabled={busy || !commentDraft.trim()} className="bg-indigo-700 hover:bg-indigo-800 text-white" data-testid="comment-submit-btn">
                      {busy ? <CircleNotch size={14} className="mr-2 animate-spin" /> : <ChatCircleDots size={14} className="mr-2" />}
                      Post note
                    </Button>
                  </div>
                </div>
              </section>

              {/* Missing-doc flags */}
              {selectedFull.missing_docs && selectedFull.missing_docs.length > 0 && (
                <section>
                  <h3 className="text-sm font-semibold text-slate-900 mb-2">Missing-doc history</h3>
                  <div className="space-y-2">
                    {selectedFull.missing_docs.map(m => (
                      <div key={m.id} className={`p-3 rounded border text-sm ${m.resolved ? 'bg-emerald-50 border-emerald-200' : 'bg-amber-50 border-amber-200'}`}>
                        <div className="flex items-center justify-between">
                          <p className="font-medium text-slate-900">{m.doc_type}</p>
                          <Badge className={m.resolved ? 'bg-emerald-200 text-emerald-900 border-emerald-300 border' : 'bg-amber-200 text-amber-900 border-amber-300 border'}>
                            {m.resolved ? 'Resolved' : 'Open'}
                          </Badge>
                        </div>
                        {m.note && <p className="text-xs text-slate-700 mt-1">Note: {m.note}</p>}
                        <p className="text-[10px] text-slate-500 mt-1">Flagged {m.created_at ? new Date(m.created_at).toLocaleDateString() : ''} by {m.flagged_by_name || m.flagged_by || 'you'}</p>
                        {m.resolved && m.resolution_note && (
                          <p className="text-xs text-emerald-800 mt-1">Recruiter: {m.resolution_note}</p>
                        )}
                      </div>
                    ))}
                  </div>
                </section>
              )}
            </div>
          )}
        </SheetContent>
      </Sheet>

      {/* Flag Missing Doc dialog */}
      <Dialog open={flagOpen} onOpenChange={setFlagOpen}>
        <DialogContent data-testid="flag-missing-dialog">
          <DialogHeader>
            <DialogTitle>Flag a missing document</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs">Document type / name</Label>
              <Input placeholder="e.g. Grade 12 marksheet, IELTS score report…" value={flagDocType} onChange={(e) => setFlagDocType(e.target.value)} data-testid="flag-doc-type-input" />
            </div>
            <div>
              <Label className="text-xs">Note to the recruiter (optional)</Label>
              <Textarea rows={4} placeholder="Explain exactly what's needed — a clear scan, translated version, updated version, etc." value={flagNote} onChange={(e) => setFlagNote(e.target.value)} data-testid="flag-note-input" />
            </div>
            <p className="text-xs text-slate-500">
              The recruiter will be emailed and see this flag in their portal. The student cannot advance until they mark it resolved.
            </p>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setFlagOpen(false)}>Cancel</Button>
            <Button onClick={doFlagMissing} disabled={busy || !flagDocType.trim()} className="bg-amber-500 hover:bg-amber-600 text-white" data-testid="flag-submit-btn">
              {busy ? <CircleNotch size={14} className="mr-2 animate-spin" /> : <WarningCircle size={14} className="mr-2" />} Flag & notify recruiter
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Financials modal (institution 1/2 Money Received flow) */}
      {selectedFull && (
        <FinancialsModal
          open={financialsOpen}
          onOpenChange={setFinancialsOpen}
          student={selectedFull}
          mode={financialsMode}
          onSaved={({ new_stage, financials, financials_updated_at }) => {
            setSelectedFull(sf => sf ? {
              ...sf,
              ...(new_stage ? { application_stage: new_stage } : {}),
              financials, financials_updated_at,
            } : sf);
            // Refresh the list + detail so stage pill + KPIs update everywhere.
            load();
          }}
        />
      )}

      {/* EA send dialog */}
      <PrepareEAModal
        open={prepareEaOpen}
        onOpenChange={setPrepareEaOpen}
        student={selected}
        onSent={(res) => {
          // Optimistic UI update — advance stage locally so table & KPIs refresh.
          setStudents(prev => prev.map(s => s.id === selected?.id ? { ...s, application_stage: 'enrollment_agreement_sent', ea_envelope_id: res.envelope_id } : s));
          setSelected(prev => prev ? { ...prev, application_stage: 'enrollment_agreement_sent', ea_envelope_id: res.envelope_id } : prev);
        }}
      />
      <BulkPrepareEAModal
        open={bulkEaOpen}
        onOpenChange={setBulkEaOpen}
        mode={bulkEaMode}
        students={students.filter(s => selectedIds.includes(s.id))}
        onSent={(res) => {
          const sentIds = (res?.results || []).filter(r => r.ok).map(r => r.student_id);
          if (sentIds.length) {
            setStudents(prev => prev.map(s => sentIds.includes(s.id) ? { ...s, application_stage: 'enrollment_agreement_sent' } : s));
            setSelectedIds([]);
          }
        }}
      />
      <BulkEaFromSheetModal
        open={bulkEaSheetOpen}
        onClose={() => setBulkEaSheetOpen(false)}
        onSent={() => {
          // Refresh the student list so newly-sent EAs show the "EA Sent"
          // pipeline stage. Cheaper than trying to figure out which IDs
          // moved (the send job can skip students for many reasons).
          load();
        }}
      />

      <Dialog open={eaOpen} onOpenChange={setEaOpen}>
        <DialogContent className="max-w-2xl" data-testid="ea-dialog">
          <DialogHeader>
            <DialogTitle>Send Enrollment Agreement</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs">Subject</Label>
              <Input value={eaSubject} onChange={(e) => setEaSubject(e.target.value)} data-testid="ea-subject-input" />
            </div>
            <div>
              <Label className="text-xs">Message body (editable)</Label>
              <Textarea rows={10} value={eaBody} onChange={(e) => setEaBody(e.target.value)} data-testid="ea-body-input" />
            </div>
            <div>
              <Label className="text-xs">Attach EA PDF (with DocuSign anchor tags {'{{sign_here}}, {{full_name}}, {{date_signed}}'})</Label>
              <Input type="file" accept="application/pdf" onChange={(e) => setEaFile(e.target.files?.[0] || null)} data-testid="ea-pdf-input" />
              {eaFile && <p className="text-[10px] text-slate-500 mt-1">Selected: {eaFile.name} · {(eaFile.size / 1024).toFixed(1)} KB</p>}
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setEaOpen(false)}>Cancel</Button>
            <Button onClick={sendEa} disabled={busy || !eaFile} className="bg-indigo-600 hover:bg-indigo-700 text-white" data-testid="ea-send-btn">
              {busy ? <CircleNotch size={14} className="mr-2 animate-spin" /> : <PaperPlaneTilt size={14} className="mr-2" />} Send via DocuSign
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* COE send dialog */}
      <Dialog open={coeOpen} onOpenChange={setCoeOpen}>
        <DialogContent className="max-w-2xl" data-testid="coe-dialog">
          <DialogHeader>
            <DialogTitle>Send Confirmation of Enrollment (COE)</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs">Subject</Label>
              <Input value={coeSubject} onChange={(e) => setCoeSubject(e.target.value)} data-testid="coe-subject-input" />
            </div>
            <div>
              <Label className="text-xs">Message body</Label>
              <Textarea rows={10} value={coeBody} onChange={(e) => setCoeBody(e.target.value)} data-testid="coe-body-input" />
            </div>
            <div>
              <Label className="text-xs">Attach COE PDF</Label>
              <Input type="file" accept="application/pdf" onChange={(e) => setCoeFile(e.target.files?.[0] || null)} data-testid="coe-pdf-input" />
              {coeFile && <p className="text-[10px] text-slate-500 mt-1">Selected: {coeFile.name} · {(coeFile.size / 1024).toFixed(1)} KB</p>}
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setCoeOpen(false)}>Cancel</Button>
            <Button onClick={sendCoe} disabled={busy || !coeFile} className="bg-indigo-600 hover:bg-indigo-700 text-white" data-testid="coe-send-btn">
              {busy ? <CircleNotch size={14} className="mr-2 animate-spin" /> : <PaperPlaneTilt size={14} className="mr-2" />} Send COE
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk EA/COE upload dialog — one flow, target stage picker */}
      <Dialog open={showCoeUpload} onOpenChange={(o) => { if (!o) { setShowCoeUpload(false); setCoeFileToUpload(null); setCoeResult(null); } }}>
        <DialogContent className="max-w-lg" data-testid="institution-coe-upload-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><UploadSimple size={18} className="text-emerald-600" /> Bulk EA / COE Sync</DialogTitle>
            <DialogDescription>
              Upload an Excel or CSV listing students. We&rsquo;ll match rows by <strong>email</strong> or <strong>name</strong> against students at your college and flip each match to the stage you pick. Recruiters get an instant bell + email for every student updated.
            </DialogDescription>
          </DialogHeader>

          {!coeResult && (
            <div className="space-y-3">
              {/* Target stage picker */}
              <div>
                <p className="text-xs font-semibold text-slate-700 mb-1.5">What are you marking?</p>
                <div className="inline-flex rounded-lg border border-slate-200 bg-white overflow-hidden" data-testid="bulk-stage-picker">
                  <button
                    type="button"
                    onClick={() => setBulkTargetStage('enrollment_agreement_signed')}
                    className={`px-4 py-2 text-xs font-semibold transition-colors ${bulkTargetStage === 'enrollment_agreement_signed' ? 'bg-emerald-600 text-white' : 'text-slate-600 hover:bg-slate-50'}`}
                    data-testid="bulk-stage-ea-signed"
                  >EA Signed</button>
                  <button
                    type="button"
                    onClick={() => setBulkTargetStage('coe_sent')}
                    className={`px-4 py-2 text-xs font-semibold transition-colors border-l border-slate-200 ${bulkTargetStage === 'coe_sent' ? 'bg-emerald-600 text-white' : 'text-slate-600 hover:bg-slate-50'}`}
                    data-testid="bulk-stage-coe-sent"
                  >COE Sent</button>
                </div>
                <p className="text-[10px] text-slate-500 mt-1.5">
                  {bulkTargetStage === 'enrollment_agreement_signed'
                    ? 'Students in this sheet already signed their Enrollment Agreement.'
                    : 'Students in this sheet have received their Confirmation of Enrollment.'}
                </p>
              </div>

              <div className="rounded-lg border-2 border-dashed border-slate-300 bg-slate-50 p-6 text-center hover:border-emerald-400 transition-colors">
                <Input
                  type="file"
                  accept=".xlsx,.xls,.csv"
                  onChange={(e) => setCoeFileToUpload(e.target.files?.[0] || null)}
                  className="max-w-sm mx-auto"
                  data-testid="institution-coe-file-input"
                />
                {coeFileToUpload && (
                  <p className="text-xs text-slate-600 mt-2 truncate">Selected: <strong>{coeFileToUpload.name}</strong></p>
                )}
              </div>
              <div className="rounded-lg bg-amber-50 border border-amber-200 p-3 text-[11px] text-amber-900">
                <strong>Tip:</strong> your sheet needs at least one of these columns — <code>Email</code>, <code>Name</code>, or <code>First Name</code> + <code>Last Name</code>. Any other columns are ignored. Students already at or past the target stage (and rejected students) are skipped safely — the sync never moves anyone backwards.
              </div>
            </div>
          )}

          {coeResult && (
            <div className="space-y-3" data-testid="coe-upload-result">
              <div className="grid grid-cols-2 gap-3">
                <div className="rounded-lg bg-emerald-50 border border-emerald-200 p-3">
                  <p className="text-[10px] uppercase tracking-wider text-emerald-700 font-bold">Updated</p>
                  <p className="text-2xl font-semibold text-emerald-900 tabular-nums">{coeResult.updated}</p>
                </div>
                <div className="rounded-lg bg-slate-50 border border-slate-200 p-3">
                  <p className="text-[10px] uppercase tracking-wider text-slate-500 font-bold">Rows in sheet</p>
                  <p className="text-2xl font-semibold text-slate-800 tabular-nums">{coeResult.total_rows}</p>
                </div>
                <div className="rounded-lg bg-slate-50 border border-slate-200 p-3">
                  <p className="text-[10px] uppercase tracking-wider text-slate-500 font-bold">Already at / past target</p>
                  <p className="text-xl font-semibold text-slate-700 tabular-nums">{coeResult.already_at_stage}</p>
                </div>
                <div className="rounded-lg bg-slate-50 border border-slate-200 p-3">
                  <p className="text-[10px] uppercase tracking-wider text-slate-500 font-bold">Unmatched</p>
                  <p className="text-xl font-semibold text-slate-700 tabular-nums">{coeResult.unmatched}</p>
                </div>
              </div>
              {coeResult.skipped_terminal > 0 && (
                <p className="text-[11px] text-amber-800">{coeResult.skipped_terminal} row(s) were skipped because those students are marked as rejected.</p>
              )}
              {Array.isArray(coeResult.unmatched_rows) && coeResult.unmatched_rows.length > 0 && (
                <details className="rounded-lg border border-slate-200 bg-white" open>
                  <summary className="cursor-pointer px-3 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-50 rounded-lg" data-testid="unmatched-rows-toggle">
                    Show unmatched rows ({coeResult.unmatched_rows.length})
                  </summary>
                  <div className="border-t border-slate-100 max-h-60 overflow-y-auto text-[11px]">
                    <table className="w-full">
                      <thead className="bg-slate-50 border-b border-slate-100 sticky top-0">
                        <tr>
                          <th className="px-3 py-1.5 text-left text-[9px] uppercase tracking-wider text-slate-500">Name</th>
                          <th className="px-3 py-1.5 text-left text-[9px] uppercase tracking-wider text-slate-500">Email</th>
                        </tr>
                      </thead>
                      <tbody>
                        {coeResult.unmatched_rows.map((r, i) => (
                          <tr key={i} className="border-b border-slate-50 last:border-b-0" data-testid={`unmatched-row-${i}`}>
                            <td className="px-3 py-1.5 text-slate-800 truncate max-w-[180px]">{r.name || <span className="text-slate-400">—</span>}</td>
                            <td className="px-3 py-1.5 text-slate-600 truncate max-w-[220px]">{r.email || <span className="text-slate-400">—</span>}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <div className="p-2 border-t border-slate-100 text-[10px] text-slate-500 leading-relaxed">
                    <strong>Common reasons:</strong> student is at a different college · student was never added to the portal · email or name spelled differently in the sheet vs the profile.
                  </div>
                </details>
              )}
            </div>
          )}

          <DialogFooter>
            {coeResult ? (
              <>
                <Button variant="ghost" onClick={() => { setCoeResult(null); setCoeFileToUpload(null); }} data-testid="coe-upload-again-btn">Upload another</Button>
                <Button onClick={() => setShowCoeUpload(false)} className="bg-emerald-600 hover:bg-emerald-700 text-white">Done</Button>
              </>
            ) : (
              <>
                <Button variant="ghost" onClick={() => setShowCoeUpload(false)}>Cancel</Button>
                <Button onClick={submitCoeUpload} disabled={!coeFileToUpload || coeUploading} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="coe-upload-submit-btn">
                  {coeUploading ? <><CircleNotch size={14} className="mr-2 animate-spin" /> Syncing…</> : <><UploadSimple size={14} className="mr-2" /> Sync now</>}
                </Button>
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Inline file preview modal */}
      <Dialog open={!!previewFile} onOpenChange={(o) => { if (!o) setPreviewFile(null); }}>
        <DialogContent className="max-w-4xl h-[85vh] p-0 overflow-hidden flex flex-col" data-testid="file-preview-modal">
          <DialogHeader className="px-5 py-3 border-b border-slate-200 flex-shrink-0">
            <DialogTitle className="flex items-center gap-2 pr-8 text-sm">
              {previewFile?.isImage ? <ImageIcon size={16} className="text-indigo-600" /> : previewFile?.isPdf ? <FilePdf size={16} className="text-rose-500" /> : <FileText size={16} className="text-slate-500" />}
              <span className="truncate">{previewFile?.original_filename || previewFile?.doc_type}</span>
              <a href={previewFile?.url} target="_blank" rel="noopener noreferrer" className="ml-auto text-xs text-indigo-600 hover:underline whitespace-nowrap" data-testid="file-preview-open-external">
                Open in new tab ↗
              </a>
            </DialogTitle>
            <DialogDescription className="sr-only">Preview of the uploaded document.</DialogDescription>
          </DialogHeader>
          <div className="flex-1 min-h-0 overflow-auto bg-slate-100 flex items-center justify-center">
            {previewFile?.isImage && (
              <img src={previewFile.url} alt={previewFile.original_filename || 'preview'} className="max-w-full max-h-full object-contain" />
            )}
            {previewFile?.isPdf && (
              <iframe src={previewFile.url} title="PDF preview" className="w-full h-full border-0 bg-white" />
            )}
            {previewFile && !previewFile.isImage && !previewFile.isPdf && (
              <div className="text-center p-10">
                <FileText size={40} className="text-slate-400 mx-auto mb-3" />
                <p className="text-sm text-slate-600">Preview not available for this file type.</p>
                <a href={previewFile.url} target="_blank" rel="noopener noreferrer" className="inline-block mt-3 px-4 py-2 text-xs bg-indigo-600 text-white rounded hover:bg-indigo-700">
                  Download to view
                </a>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
